import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Calculator, Play, Trash2 } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { couleurCout, DEFAVORABLE, FAVORABLE, NEUTRE, NEUTRE_DOUX, signe } from '../../lib/couleurs';
import { LIGNES_PRODUCTION, annulerRegularisation, bornesDeCampagne, enregistrerRegularisation, rapprochement } from '../../lib/cri';
import { Carte, btn, btnLight, inputCls, labelCls, td, th } from './ui';

const moisCourant = () => {
  const d = new Date();
  const debut = new Date(d.getFullYear(), d.getMonth(), 1);
  const fin = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return [debut.toISOString().slice(0, 10), fin.toISOString().slice(0, 10)];
};

export const RegularisationCri = ({ campagne, regularisations, utilisateur, onMaj }) => {
  const [m0, m1] = moisCourant();
  const [ligne, setLigne] = useState(LIGNES_PRODUCTION[0]);
  const [du, setDu] = useState(m0);
  const [au, setAu] = useState(m1);
  const [rap, setRap] = useState(null);

  const charger = useCallback(() => {
    rapprochement({ campagne, ligne, du, au }).then(setRap);
  }, [campagne, ligne, du, au]);

  useEffect(() => {
    charger();
  }, [charger, regularisations]);

  const comptabiliser = async (type) => {
    const bornes = bornesDeCampagne(campagne);
    const periode = type === 'cloture' ? { du: bornes.debut, au: bornes.fin } : { du, au };
    const { error, regularisation } = await enregistrerRegularisation({ campagne, ligne, ...periode, type, utilisateur });
    if (error) return toast.error(error);
    toast.success(
      `${type === 'cloture' ? 'Clôture' : 'Régularisation mensuelle'} comptabilisée : ${fmt(regularisation.ecartComptabilise, 2)} TND répartis sur ${regularisation.ajustements.length} OF.`
    );
    onMaj();
  };

  const annuler = async (id) => {
    const { error } = await annulerRegularisation(id, utilisateur);
    if (error) return toast.error(error);
    toast.success('Régularisation annulée.');
    onMaj();
  };

  return (
    <div className="space-y-4">
      <Carte
        testid="cri-rapprochement"
        titre="Rapprochement charges réelles / frais standards"
        sous="Périmètre homogène : même campagne et même ligne, OF d’achat ET de prestation. Écart = charges réelles imputables − frais standards imputés."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className={labelCls}>Ligne de production</span>
            <select data-testid="cri-reg-ligne" value={ligne} onChange={(e) => setLigne(e.target.value)} className={inputCls}>
              {LIGNES_PRODUCTION.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Du</span>
            <input data-testid="cri-reg-du" type="date" value={du} onChange={(e) => setDu(e.target.value)} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Au</span>
            <input data-testid="cri-reg-au" type="date" value={au} onChange={(e) => setAu(e.target.value)} className={inputCls} />
          </label>
          <button data-testid="cri-reg-recalculer" type="button" onClick={charger} className={`${btn} mt-6`}>
            <Calculator size={15} /> Recalculer
          </button>
        </div>

        {rap && (
          <>
            <div className="mt-4 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
              {[
                ['Charges réelles imputables', `${fmt(rap.reelles, 2)} TND`, DEFAVORABLE],
                ['Frais standards imputés', `${fmt(rap.standardsImputes, 2)} TND`, NEUTRE],
                ['Écart de période', `${signe(rap.ecart)} ${fmt(Math.abs(rap.ecart), 2)} TND`, couleurCout(rap.ecart, NEUTRE)],
                ['Déjà comptabilisé', `${fmt(rap.dejaComptabilise, 2)} TND`, NEUTRE_DOUX],
                ['Solde à régulariser', `${signe(rap.solde)} ${fmt(Math.abs(rap.solde), 2)} TND`, couleurCout(rap.solde, NEUTRE)],
              ].map(([k, v, cls]) => (
                <div key={k} className="bg-white px-3 py-2.5">
                  <p className="text-[11px] text-slate-500">{k}</p>
                  <p className={`tabular font-data text-base font-semibold ${cls}`}>{v}</p>
                </div>
              ))}
            </div>

            <div className="mt-3 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ['Tonnes imputées', `${fmt(rap.tonnes, 2)} t`],
                ['Capacité normale', rap.capaciteNormaleT ? `${fmt(rap.capaciteNormaleT, 2)} t` : '—'],
                ['Taux d’activité', rap.sousActivite.tauxActivite === null ? '—' : `${fmt(rap.sousActivite.tauxActivite * 100, 2)} %`],
                ['Fixes non absorbés (sous-activité)', `${fmt(rap.sousActivite.fixesNonAbsorbes, 2)} TND`],
              ].map(([k, v]) => (
                <div key={k} className="bg-white px-3 py-2.5">
                  <p className="text-[11px] text-slate-500">{k}</p>
                  <p className="tabular font-data text-base font-semibold text-slate-900">{v}</p>
                </div>
              ))}
            </div>

            {rap.exceptionnelles > 0 && (
              <p data-testid="cri-reg-exceptionnelles" className="mt-3 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {fmt(rap.exceptionnelles, 2)} TND de charges exceptionnelles sont isolées du CRI et présentées séparément dans le résultat de campagne.
              </p>
            )}
            {rap.ofsNonFiges.length > 0 && (
              <p data-testid="cri-reg-nonfiges" className="mt-3 border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {rap.ofsNonFiges.length} OF de la période n’ont pas encore de coût standard figé : figez-les (onglet Fiches OF) pour les inclure dans l’écart.
              </p>
            )}

            <div className="mt-4 overflow-x-auto border border-slate-200">
              <table className="w-full min-w-[720px] border-collapse">
                <thead className="bg-slate-50">
                  <tr>
                    <th className={th}>OF</th>
                    <th className={th}>Type</th>
                    <th className={th}>Tonnes traitées</th>
                    <th className={th}>Clé de répartition</th>
                    <th className={th}>ΔF proposé (TND)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rap.ajustements.length === 0 && (
                    <tr>
                      <td className={`${td} text-slate-500`} colSpan={5}>
                        Aucun OF au coût standard figé sur ce périmètre.
                      </td>
                    </tr>
                  )}
                  {rap.ajustements.map((a) => (
                    <tr key={a.ofId} data-testid={`cri-ajustement-${a.ofId}`}>
                      <td className={`${td} font-semibold`}>{a.numero}</td>
                      <td className={`${td} ${NEUTRE_DOUX}`}>{a.type === 'prestation' ? 'Prestation' : 'Achat'}</td>
                      <td className={td}>{fmt(a.tonnes, 2)}</td>
                      <td className={`${td} ${NEUTRE_DOUX}`}>{rap.tonnes > 0 ? `${fmt((a.tonnes / rap.tonnes) * 100, 2)} %` : '—'}</td>
                      <td className={`${td} font-semibold ${couleurCout(a.montant, NEUTRE)}`}>
                        {signe(a.montant)} {fmt(Math.abs(a.montant), 2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr>
                    <td className={`${td} font-bold`} colSpan={4}>
                      Total réparti (identique au solde, au millième)
                    </td>
                    <td data-testid="cri-ajustement-total" className={`${td} font-bold`}>
                      {fmt(rap.ajustements.reduce((s, a) => s + a.montant, 0), 2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button data-testid="cri-reg-mensuelle" type="button" onClick={() => comptabiliser('mensuelle')} className={btn}>
                <Play size={15} /> Comptabiliser la régularisation de la période
              </button>
              <button data-testid="cri-reg-cloture" type="button" onClick={() => comptabiliser('cloture')} className={`${btn} bg-slate-700`}>
                <Calculator size={15} /> Clôture définitive de la campagne
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              La clôture ne comptabilise que le solde restant : la relancer ne cumule jamais une seconde fois le même écart.
            </p>
          </>
        )}
      </Carte>

      <Carte testid="cri-reg-liste" titre="Régularisations comptabilisées" sous="Le coût standard initial et ses régularisations restent conservés séparément.">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                <th className={th}>Type</th>
                <th className={th}>Ligne</th>
                <th className={th}>Périmètre</th>
                <th className={th}>Charges réelles</th>
                <th className={th}>Standards imputés</th>
                <th className={th}>Écart</th>
                <th className={th}>Déjà comptabilisé</th>
                <th className={th}>Comptabilisé</th>
                <th className={th}>OF</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {regularisations.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={10}>
                    Aucune régularisation comptabilisée.
                  </td>
                </tr>
              )}
              {regularisations.map((r) => (
                <tr key={r.id} data-testid={`cri-reg-row-${r.id}`}>
                  <td className={td}>
                    <span className={`border px-1.5 py-0.5 font-data text-[11px] uppercase ${r.type === 'cloture' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-slate-50 text-slate-600'}`}>
                      {r.type === 'cloture' ? 'Clôture' : 'Mensuelle'}
                    </span>
                  </td>
                  <td className={td}>{r.ligne}</td>
                  <td className={td}>
                    {r.du} → {r.au}
                  </td>
                  <td className={`${td} ${DEFAVORABLE}`}>{fmt(r.chargesReelles, 2)}</td>
                  <td className={td}>{fmt(r.standardsImputes, 2)}</td>
                  <td className={`${td} ${couleurCout(r.ecart, NEUTRE)}`}>
                    {signe(r.ecart)} {fmt(Math.abs(r.ecart), 2)}
                  </td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{fmt(r.dejaComptabiliseAvant, 2)}</td>
                  <td className={`${td} font-semibold ${couleurCout(r.ecartComptabilise, NEUTRE)}`}>
                    {signe(r.ecartComptabilise)} {fmt(Math.abs(r.ecartComptabilise), 2)}
                  </td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{(r.ajustements ?? []).length}</td>
                  <td className="px-3 py-2">
                    <button data-testid={`cri-reg-del-${r.id}`} type="button" onClick={() => annuler(r.id)} className={btnLight}>
                      <Trash2 size={13} /> Annuler
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] text-slate-500">
          <span className={FAVORABLE}>Vert</span> = économie de charges ou marge positive · <span className={DEFAVORABLE}>rouge</span> = dépassement de charges ou marge négative. Le signe et le libellé sont toujours affichés.
        </p>
      </Carte>
    </div>
  );
};
